/**
 * Shipping protection switch (replaces the Captain Shipping Protection app).
 * Markup and settings: snippets/shipping-protection.liquid.
 * On = 1 protection in the cart, off = none. The protection never stays alone in the cart
 * and never goes above quantity 1.
 * In the cart drawer the drawer is re-rendered after each change (the switch comes back
 * rendered by Liquid with the right state); on the cart page the page reloads.
 */
class ShippingProtection extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.ship-protect__input');
    this.input?.addEventListener('change', () => this.update(this.input.checked ? 1 : 0));

    const quantity = parseInt(this.dataset.quantity, 10) || 0;
    const others = parseInt(this.dataset.otherItems, 10) || 0;
    if (quantity > 0 && others === 0) {
      this.update(0);
    } else if (quantity > 1) {
      this.update(1);
    }
  }

  async update(quantity) {
    if (this.busy) return;
    this.busy = true;
    const wasOn = (parseInt(this.dataset.quantity, 10) || 0) > 0;
    const showState = (on) => {
      this.classList.toggle('is-on', on);
      if (this.input) this.input.checked = on;
      const desc = this.querySelector('.ship-protect__desc');
      if (desc) desc.textContent = on ? this.dataset.onText : this.dataset.offText;
    };
    this.classList.add('is-loading');
    showState(quantity > 0);

    const drawer = document.querySelector('cart-drawer');
    const inDrawer = !!drawer && drawer.contains(this);

    try {
      // On page load this script can run before the cart drawer's own script.
      if (inDrawer) await customElements.whenDefined('cart-drawer');
      const body = { updates: { [this.dataset.variantId]: quantity } };
      if (inDrawer) {
        body.sections = drawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'error');

      if (!inDrawer) {
        window.location.reload();
        return;
      }
      ShippingProtection.renderDrawer(drawer, data);
    } catch (error) {
      console.error('[shipping-protection]', error);
      this.busy = false;
      this.classList.remove('is-loading');
      showState(wasOn);
    }
  }

  // Refresh the drawer without opening it when it is closed (clean-up on page load).
  static renderDrawer(drawer, data) {
    if (drawer.classList.contains('active')) {
      drawer.renderContents(data);
      return;
    }
    drawer.getSectionsToRender().forEach((section) => {
      const element = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!element || !data.sections?.[section.id]) return;
      element.innerHTML = drawer.getSectionInnerHTML(data.sections[section.id], section.selector);
    });
    drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
    drawer.syncEmptyState?.();
  }
}

if (!customElements.get('shipping-protection')) customElements.define('shipping-protection', ShippingProtection);

/**
 * The header renders the cart count with Liquid on page load, protection included.
 * Show the number of articles instead (sections/cart-icon-bubble.liquid does the same on every cart update).
 */
(() => {
  const fixHeaderCount = () => {
    const protection = document.querySelector('cart-drawer shipping-protection');
    const quantity = parseInt(protection?.dataset.quantity, 10) || 0;
    if (!quantity) return;
    const others = parseInt(protection.dataset.otherItems, 10) || 0;
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble || others === 0) return;
    const count = bubble.querySelector('span[aria-hidden="true"]');
    if (count) count.textContent = others;
    const label = bubble.querySelector('.visually-hidden');
    if (label) {
      label.textContent = label.textContent
        .replace(/\d+/, others)
        .replace(/\bitems?\b/, others === 1 ? 'item' : 'items');
    }
  };

  // Cart page: Dawn updates the items without re-rendering the switch, so remove a protection left alone here.
  const watchCartPage = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const items = event?.cartData?.items;
      const protection = document.querySelector('#main-cart-footer shipping-protection');
      if (!items || !protection) return;
      const variantId = Number(protection.dataset.variantId);
      if (items.length && items.every((item) => item.variant_id === variantId)) protection.update(0);
    });
  };

  const start = () => {
    fixHeaderCount();
    watchCartPage();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
